import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type ReactNode, useId, useState } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";

export interface CollapsibleSectionProps {
  // `id` is the anchor that the page's side navigation scrolls to.
  id?: string;
  iconName: string;
  title: string;
  count: number;
  // `summary` is shown on the right of the header, next to the toggle icon,
  // and stays visible while the section is collapsed.
  summary: ReactNode;
  dataTestId: string;
  children: ReactNode;
}

export function CollapsibleSection({
  id,
  iconName,
  title,
  count,
  summary,
  dataTestId,
  children,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();

  return (
    <Stack
      id={id}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        scrollMarginTop: (theme) => theme.spacing(10),
      }}
      data-testid={dataTestId}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((open) => !open)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          pt: 3,
          px: { xs: 2, sm: 3, lg: 6 },
          // The collapsed header has nothing below it, so equal padding keeps
          // its content vertically centred.
          pb: isOpen ? 1.5 : 3,
          border: "none",
          borderRadius: isOpen ? "12px 12px 0 0" : "12px",
          backgroundColor: "primary._states.hover",
          cursor: "pointer",
          textAlign: "left",
          font: "inherit",
          color: "text.primary",
        }}
        data-testid={`${dataTestId}-toggle-button`}
      >
        <Stack
          direction="row"
          alignItems="flex-end"
          columnGap={1.5}
          flexWrap="wrap"
          sx={{ pb: 0.5 }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <PublicSvgIcon name={iconName} />
            <Typography variant="h5" component="span">
              {title}
            </Typography>
          </Stack>
          <Typography
            variant="body1"
            component="span"
            sx={{ color: "text.secondary", pb: 0.25 }}
            data-testid={`${dataTestId}-count`}
          >
            {count}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="flex-start" gap={1}>
          <Box data-testid={`${dataTestId}-total`}>{summary}</Box>
          <PublicSvgIcon
            name="keyboard_arrow_up"
            inheritColor
            sx={{
              display: "flex",
              transform: isOpen ? "none" : "rotate(180deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </Stack>
      </Box>
      <Collapse in={isOpen} id={contentId} unmountOnExit>
        <Box sx={{ pt: 2.5, pb: 3, px: { xs: 2, sm: 3, lg: 6 } }}>
          {children}
        </Box>
      </Collapse>
    </Stack>
  );
}
